import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import { useParams } from 'react-router-dom';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import React from 'react';

const MENU_PROPS = {
  PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 250 } },
};

// Defined outside component — stable reference, no re-creation on render
const TOGGLE_OPTIONS = [
  { value: 'multiple', icon: <FormatListNumberedIcon sx={{ fontSize: 16 }} />, label: 'Multiple' },
  { value: 'single', icon: <LooksOneIcon sx={{ fontSize: 16 }} />, label: 'Single' },
];

const AiInputScreen = ({
  aiDescription,
  setAiDescription,
  aiGenerating,
  aiError,
  mode,
  onModeChange,
  testSuites = [],
  selectedTestSuites = [],
  onTestSuitesChange,
}) => { const { projectId } = useParams();
return(
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <AutoAwesomeIcon color="primary" />
      <Typography variant="subtitle1" fontWeight={600}>Describe your test scenario</Typography>
    </Box>

    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.75, display: 'block' }}>
        How many test cases do you need?
      </Typography>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(_, val) => { if (val) onModeChange(val); }}
        disabled={aiGenerating}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            px: 2, fontSize: '13px', fontWeight: 600, textTransform: 'none',
            borderColor: '#e5e7eb',
            '&.Mui-selected': { bgcolor: '#EFF6FF', color: 'primary.main', borderColor: 'primary.main' },
          },
        }}
      >
        {TOGGLE_OPTIONS.map(({ value, icon, label }) => (
          <ToggleButton key={value} value={value} sx={{ gap: 0.75 }}>
            {icon}{label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {mode === 'multiple'
          ? 'AI will generate a full suite covering happy path, edge cases, and negatives.'
          : 'AI will generate one focused test case for your scenario.'}
      </Typography>
    </Box>

    <TextField
      multiline
      rows={4}
      fullWidth
      label="Test Scenario"
      placeholder="e.g. User should be able to reset password via email link…"
      value={aiDescription}
      onChange={(e) => { if (e.target.value.length <= 500) setAiDescription(e.target.value); }}
      helperText={`${aiDescription.length}/500 characters`}
      disabled={aiGenerating}
      autoFocus
    />

    {mode === 'multiple' && (
      <Box>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
          Test Suite <span style={{ color: '#ef4444' }}>*</span>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          Applies to all generated test cases
        </Typography>
        <FormControl fullWidth>
          <Select
            multiple
            displayEmpty
            value={selectedTestSuites}
            onChange={(e) => {
              const val = e.target.value;
              onTestSuitesChange(typeof val === 'string' ? val.split(',') : val);
            }}
            input={<OutlinedInput />}
            IconComponent={KeyboardArrowDownIcon}
            disabled={aiGenerating}
            inputProps={{ 'aria-label': 'Select test suites' }}
            renderValue={(selected) =>
              selected.length === 0
                ? <span style={{ color: '#9e9e9e' }}>Select test suite(s)</span>
                : testSuites.filter((s) => selected.includes(s.id)).map((s) => s.name).join(', ')
            }
            MenuProps={MENU_PROPS}
            sx={{ maxHeight: '48px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ced4da' } }}
          >
            {testSuites.map((suite) => (
              <MenuItem key={suite.id} value={suite.id} disabled={suite.status === 'suspended'}>
                <Checkbox disableRipple checked={selectedTestSuites.includes(suite.id)} disabled={suite.status === 'suspended'} />
                <ListItemText
                  primary={
                    <span>
                      {suite.name}
                      {suite.status === 'suspended' && <span style={{ fontSize: '10px', color: 'red' }}> (Suspended)</span>}
                    </span>
                  }
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
           <a
            href={`/project/${projectId}/testsuites`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: '#1976d2', textDecoration: 'none' }}
          >
            + Add test suite
          </a>
      </Box>
    )}

    {aiError && <Alert severity="error" sx={{ py: 0 }}>{aiError}</Alert>}
  </Box>
);
}

export default AiInputScreen;